'use client';

import type { RealtimeVoiceCallControllerArgs } from './realtime-voice-call-controller-args';
import type { LiveVoiceSession } from '@/lib/live-voice-session';
import { useLocale } from 'next-intl';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { Id } from '@/convex/_generated/dataModel';
import type { OrgChatQueueItem } from '@/components/org-chat/org-chat-queue-controls';
import {
  finishRealtimeCallGracefully,
  scheduleRealtimeLeaseExpiry,
} from '@/components/org-chat/realtime-voice-lifecycle';
import { startRealtimeVoiceCall } from '@/components/org-chat/start-realtime-voice-call';
import { useRealtimeQuestionnairePrompts } from '@/components/org-chat/use-realtime-questionnaire-prompts';
import { useRealtimeResponseController } from '@/components/org-chat/use-realtime-response-controller';
import { useVoiceCallUiController } from '@/components/org-chat/use-voice-call-ui-controller';
import { useVoiceDelegationReceipts } from '@/components/org-chat/use-voice-delegation-receipts';
import { useVoiceRecordingController } from '@/components/org-chat/use-voice-recording-controller';
import { useOrgChatInterfaceOptional } from '@/contexts/org-chat-interface-context';
import type { RealtimeVoiceDelegationTarget } from '@/lib/realtime-voice-agent';
import { getAskQuestionCopy } from '@/i18n/ask-question-copy';
import { getRealtimeVoiceCallCopy } from '@/i18n/realtime-voice-call-copy';
import { getSweepUiCopy } from '@/i18n/sweep-ui-copy';
import { useRealtimeVoiceBackendActions } from './use-realtime-voice-backend-actions';
import { useRealtimeVoiceNavigationActions } from './use-realtime-voice-navigation-actions';
import { useOffPageVoiceContext } from '@/components/org-chat/use-off-page-voice-context';
import { useRealtimeVoiceStatusUpdates } from './use-realtime-voice-status-updates';

const GRACEFUL_END_TIMEOUT_MS = 18_000;

export function useRealtimeVoiceCallController(
  args: RealtimeVoiceCallControllerArgs
) {
  const onClose = args.onClose;
  const locale = useLocale();
  const askQuestionCopy = useMemo(() => getAskQuestionCopy(locale), [locale]);
  const uiCopy = getSweepUiCopy(locale);
  const voiceCopy = getRealtimeVoiceCallCopy(locale);
  const orgChat = useOrgChatInterfaceOptional();
  const delegationTarget = args.delegationTarget ?? orgChat;
  const {
    createSession,
    reportLiveUsage,
    heartbeatLease,
    endLease,
    getChatContext,
    sendChatMessage,
    getOffPageQuestion,
    runNativeWebSearch,
    runQuickResearch,
    answerAskQuestion,
    answerAskQuestionGroup,
  } = useRealtimeVoiceBackendActions();
  const navigationActions = useRealtimeVoiceNavigationActions();
  const [connectionVersion, setConnectionVersion] = useState(0);
  const [leaseVersion, setLeaseVersion] = useState(0);
  const [activeLeaseSessionId, setActiveLeaseSessionId] = useState<
    string | null
  >(null);
  const [queueActionId, setQueueActionId] = useState<string | null>(null);
  const [isEnding, setIsEnding] = useState(false);

  const orgThreadId =
    args.orgThreadId ??
    (orgChat?.orgThread?._id as Id<'orgThreads'> | undefined);
  const sessionRef = useRef<LiveVoiceSession | null>(null);
  const ui = useVoiceCallUiController({
    open: args.open,
    sessionId: args.sessionId,
    sessionRef,
    levelsRef: args.levelsRef,
    enableGlobalPttKeyboard: !args.floating,
  });
  const responses = useRealtimeResponseController(sessionRef);
  const recording = useVoiceRecordingController({
    open: args.open,
    orgThreadId,
    orgSlug: args.orgSlug ?? orgChat?.orgSlug ?? '',
    organizationId: args.organizationId ?? orgChat?.orgThread?.organizationId,
  });
  const receipts = useVoiceDelegationReceipts(
    args.open,
    args.queuedMessages ?? orgChat?.queuedMessages
  );
  const questionnaire = useRealtimeQuestionnairePrompts({
    open: args.open,
    orgThreadId: orgThreadId ? String(orgThreadId) : undefined,
    sessionRef,
    queueOrSendInternalMessage: responses.queueOrSendInternalMessage,
  });
  const controllersRef = useRef({
    ui,
    responses,
    recording,
    receipts,
    questionnaire,
  });
  useEffect(() => {
    controllersRef.current = {
      ui,
      responses,
      recording,
      receipts,
      questionnaire,
    };
  }, [questionnaire, receipts, recording, responses, ui]);

  const mountedRef = useRef(false);
  const sourceChatVisibleRef = useRef(Boolean(args.isSourceChatVisible));
  const delegationTargetRef = useRef(delegationTarget);
  const callAbortRef = useRef<AbortController | null>(null);
  const activeCallTokenRef = useRef(0);
  const leaseSessionIdRef = useRef<string | null>(null);
  const callLeaseSessionIdRef = useRef<string | null>(null);
  const cancelLeaseExpiryRef = useRef<(() => void) | null>(null);
  const normalEndInFlightRef = useRef(false);
  const callOpenedAtRef = useRef<number | null>(null);
  const lastStatusEventIdRef = useRef<string | null>(null);
  const latestStatusEventIdRef = useRef<string | null>(null);
  const trackedUsageEventsRef = useRef<Set<string>>(new Set());
  const pendingUsageReportsRef = useRef<Set<Promise<void>>>(new Set());

  useEffect(() => {
    delegationTargetRef.current = delegationTarget;
  }, [delegationTarget]);
  useEffect(() => {
    sourceChatVisibleRef.current = Boolean(args.isSourceChatVisible);
  }, [args.isSourceChatVisible]);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);
  useEffect(() => {
    latestStatusEventIdRef.current = args.realtimeStatus?.turnId ?? null;
  }, [args.realtimeStatus?.turnId]);

  const clearLeaseExpiryTimer = useCallback(() => {
    cancelLeaseExpiryRef.current?.();
    cancelLeaseExpiryRef.current = null;
  }, []);

  const endActiveLease = useCallback(() => {
    clearLeaseExpiryTimer();
    const leaseSessionId = leaseSessionIdRef.current;
    leaseSessionIdRef.current = null;
    setActiveLeaseSessionId(null);
    if (!leaseSessionId || !orgThreadId) return;
    void endLease({ orgThreadId, sessionId: leaseSessionId }).catch((error) => {
      console.warn('[voice-call] failed to end realtime lease', {
        errorName: error instanceof Error ? error.name : 'UnknownError',
      });
    });
  }, [clearLeaseExpiryTimer, endLease, orgThreadId]);

  const scheduleLeaseExpiry = useCallback(
    (leaseDurationMs: number, onExpiry: () => void) => {
      clearLeaseExpiryTimer();
      cancelLeaseExpiryRef.current = scheduleRealtimeLeaseExpiry({
        leaseDurationMs,
        onExpiry: () => {
          cancelLeaseExpiryRef.current = null;
          onExpiry();
        },
      });
    },
    [clearLeaseExpiryTimer]
  );

  const handleEnd = useCallback(() => {
    if (normalEndInFlightRef.current) return;
    normalEndInFlightRef.current = true;
    const endingCallToken = activeCallTokenRef.current;
    setIsEnding(true);
    controllersRef.current.ui.setPttActive(false);
    controllersRef.current.ui.setIsCapturing(false);
    controllersRef.current.ui.setMicMuted(true);
    void finishRealtimeCallGracefully({
      isCurrent: () =>
        mountedRef.current && activeCallTokenRef.current === endingCallToken,
      stopInput: () => {
        try {
          sessionRef.current?.mute(true);
        } catch (error) {
          console.warn('[voice-call] failed to stop realtime input cleanly', {
            errorName: error instanceof Error ? error.name : 'UnknownError',
          });
        }
      },
      drainUsage: async () => {
        const finalized = await sessionRef.current?.finish();
        if (finalized === false)
          console.warn('[voice-call] Live finalization incomplete');
        while (pendingUsageReportsRef.current.size > 0) {
          await Promise.allSettled([...pendingUsageReportsRef.current]);
        }
      },
      invalidate: () => {
        const controllers = controllersRef.current;
        activeCallTokenRef.current += 1;
        controllers.recording.finishSession();
        callAbortRef.current?.abort();
        callAbortRef.current = null;
        callOpenedAtRef.current = null;
        controllers.ui.callConnectedAtRef.current = null;
        controllers.ui.setCallElapsedMs(0);
        controllers.ui.micUnmutedAtRef.current = null;
        controllers.responses.reset();
        controllers.questionnaire.reset();
        controllers.receipts.clearReceipts();
        sessionRef.current?.close();
        sessionRef.current = null;
        endActiveLease();
        controllers.ui.setStatus('idle');
        controllers.ui.setIsCapturing(false);
        controllers.ui.setPttActive(false);
        normalEndInFlightRef.current = false;
        onClose();
      },
      timeoutMs: GRACEFUL_END_TIMEOUT_MS,
    }).catch((error: unknown) => {
      console.warn('[voice-call] final usage drain failed', {
        errorName: error instanceof Error ? error.name : 'UnknownError',
      });
    });
  }, [endActiveLease, onClose]);

  useEffect(() => {
    if (
      !args.open ||
      !orgThreadId ||
      !args.sessionId ||
      !delegationTargetRef.current
    )
      return;
    const controllers = controllersRef.current;
    if (controllers.recording.settingsLoading) return;
    const abortController = new AbortController();
    normalEndInFlightRef.current = false;
    setIsEnding(false);
    const callToken = ++activeCallTokenRef.current;
    const isCurrentCall = () =>
      activeCallTokenRef.current === callToken &&
      !abortController.signal.aborted &&
      mountedRef.current;
    callAbortRef.current = abortController;
    callOpenedAtRef.current = Date.now();
    callLeaseSessionIdRef.current = null;
    controllers.ui.callConnectedAtRef.current = null;
    controllers.ui.setCallElapsedMs(0);
    lastStatusEventIdRef.current = latestStatusEventIdRef.current;
    controllers.ui.micUnmutedAtRef.current = null;
    controllers.responses.reset();
    controllers.recording.sessionRef.current = null;
    controllers.recording.remoteStreamRef.current = null;
    controllers.recording.assistantPlaybackActiveRef.current = false;
    controllers.recording.assistantRecordingActiveRef.current = false;
    controllers.recording.sequenceRef.current = 0;
    queueMicrotask(() => {
      if (!isCurrentCall()) return;
      controllers.ui.setStatus('connecting');
      controllers.ui.setErrorText(null);
    });

    void startRealtimeVoiceCall({
      orgThreadId,
      sessionId: args.sessionId,
      locale,
      askQuestionCopy,
      voiceCopy,
      abortController,
      isCurrentCall,
      refs: {
        mountedRef,
        sourceChatVisibleRef,
        delegationTargetRef:
          delegationTargetRef as React.MutableRefObject<RealtimeVoiceDelegationTarget>,
        sessionRef,
        leaseSessionIdRef,
        lastStatusEventIdRef,
        trackedUsageEventsRef,
        pendingUsageReportsRef,
      },
      actions: {
        createSession,
        reportLiveUsage,
        heartbeatLease,
        endLease,
        getChatContext,
        sendChatMessage,
        getOffPageQuestion,
        runNativeWebSearch,
        runQuickResearch,
        answerAskQuestion,
        answerAskQuestionGroup,
      },
      navigationActions,
      recording: controllers.recording,
      responses: controllers.responses,
      questionnaire: controllers.questionnaire,
      ui: controllers.ui,
      receipts: controllers.receipts,
      endActiveLease,
      scheduleLeaseExpiry,
      onLeaseStarted: () => {
        callLeaseSessionIdRef.current = leaseSessionIdRef.current;
        setActiveLeaseSessionId(leaseSessionIdRef.current);
        setLeaseVersion((version) => version + 1);
      },
      onConnected: () => setConnectionVersion((version) => version + 1),
    });

    return () => {
      abortController.abort();
      if (activeCallTokenRef.current !== callToken) return;
      activeCallTokenRef.current += 1;
      controllers.recording.finishSession();
      callAbortRef.current = null;
      callOpenedAtRef.current = null;
      controllers.ui.callConnectedAtRef.current = null;
      controllers.ui.setCallElapsedMs(0);
      controllers.ui.micUnmutedAtRef.current = null;
      controllers.responses.reset();
      controllers.questionnaire.reset();
      controllers.receipts.clearReceipts();
      sessionRef.current?.close();
      sessionRef.current = null;
      endActiveLease();
      controllers.ui.setStatus('idle');
      controllers.ui.setIsCapturing(false);
      controllers.ui.setPttActive(false);
    };
  }, [
    answerAskQuestion,
    answerAskQuestionGroup,
    args.open,
    args.sessionId,
    askQuestionCopy,
    createSession,
    endActiveLease,
    endLease,
    getChatContext,
    sendChatMessage,
    getOffPageQuestion,
    heartbeatLease,
    locale,
    navigationActions,
    orgThreadId,
    reportLiveUsage,
    recording.settingsLoading,
    runNativeWebSearch,
    runQuickResearch,
    scheduleLeaseExpiry,
    voiceCopy,
  ]);

  useRealtimeVoiceStatusUpdates({
    open: args.open,
    orgThreadId,
    realtimeStatus: args.realtimeStatus,
    connectionVersion,
    leaseVersion,
    activeLeaseSessionId,
    sessionRef,
    controllersRef,
    leaseSessionIdRef,
    callLeaseSessionIdRef,
    callOpenedAtRef,
    lastStatusEventIdRef,
    onEnd: handleEnd,
    billingError: voiceCopy.billingError,
  });

  useOffPageVoiceContext({
    open: args.open,
    isSourceChatVisible: args.isSourceChatVisible,
    orgThreadId,
    pageContext: args.pageContext,
    statusTurnId: args.realtimeStatus?.turnId,
    pendingQuestionRevision: args.pendingQuestionRevision,
    connectionVersion,
    leaseVersion,
    sessionRef,
    leaseSessionIdRef,
    getOffPageQuestion,
    responsesRef: controllersRef,
  });

  const queueItems = useMemo<Array<OrgChatQueueItem>>(
    () =>
      (args.queuedMessages ?? orgChat?.queuedMessages ?? []).map((item) => ({
        ...item,
      })) as Array<OrgChatQueueItem>,
    [args.queuedMessages, orgChat?.queuedMessages]
  );
  return {
    ...ui,
    setupError:
      args.open && (!orgThreadId || !args.sessionId)
        ? voiceCopy.setupRequired
        : null,
    recordUserAudio: recording.recordUserAudio,
    recordAssistantAudio: recording.recordAssistantAudio,
    voiceCopy,
    uiCopy,
    queueItems,
    queueActionId,
    setQueueActionId,
    receipts: receipts.receipts,
    orgChat: orgChat!,
    isEnding,
    handleEnd,
  };
}
